import {
  COUNTRY_CODES,
  EMPLOYMENT_LABELS,
  EMPLOYMENT_TYPES,
  REGION_TAGS,
  SENIORITIES,
  SENIORITY_LABELS,
  TIMEZONES,
  curatedRoleInputSchema,
  groupRoleTypesFor,
  roleTypeLabel,
  groupSkillsFor,
  suggestedHeading,
  suggestedSkills,
  regionLabel,
  roleInputSchema,
  utcOffsetLabel,
  type CuratedRoleInput,
  type EmploymentType,
  type RoleInput,
  type Seniority,
} from '@remote-medium/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from 'cn';
import {
  BanknoteIcon,
  Clock3Icon,
  GlobeIcon,
  InfoIcon,
  SearchIcon,
  TicketIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';

import { Badge, badgeVariants } from './components/badge.tsx';
import {
  SalaryPeriodToggle,
  salaryForPeriod,
  salaryToAnnual,
  type SalaryPeriod,
} from './SalaryPeriod.tsx';
import { AddRoleType, type AddedRoleType } from './AddRoleType.tsx';
import { AddSkill, type AddedSkill } from './AddSkill.tsx';
import { SearchableSelect } from './SearchableSelect.tsx';
import { Button } from './components/button.tsx';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from './components/combobox.tsx';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from './components/field.tsx';
import { Input } from './components/input.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from './components/tooltip.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/select.tsx';
import { Textarea } from './components/textarea.tsx';

interface RoleFormTaxonomy {
  /** Approved types plus, where the caller has any, their own pending/rejected ones (Q77). */
  roleTypes: AddedRoleType[];
  skills: AddedSkill[];
  industries: { id: string; name: string }[];
}

// Loose on purpose: API views carry plain strings for enums.
interface RoleFormInitial {
  title?: string;
  roleTypeId?: string;
  industryId?: string | null;
  seniority?: string | null;
  employmentType?: string | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  currency?: string | null;
  overlapHours?: number | null;
  timezone?: string | null;
  summary?: string | null;
  skills?: { skillId: string; required: boolean }[];
  regions?: string[];
  sourceUrl?: string | null;
  sourceCompanyName?: string | null;
}

// Superset of RoleInput/CuratedRoleInput. Whichever schema validates
// (roleInputSchema vs curatedRoleInputSchema) strips the fields it doesn't
// declare, so a single form shape works for both.
type FormValues = {
  title: string;
  roleTypeId: string;
  industryId: string | null;
  seniority: Seniority | null;
  employmentType: EmploymentType | null;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string | null;
  overlapHours: number | null;
  timezone: string | null;
  summary: string;
  skills: { skillId: string; required: boolean }[];
  regions: string[];
  sourceUrl: string;
  sourceCompanyName: string;
};

// Current UTC offset, e.g. "UTC+5:30" (DST-aware as of page load). The label
// falls back to the zone name itself when Intl can't resolve an offset.
const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => {
  const offset = utcOffsetLabel(tz);
  return { value: tz, label: offset !== tz ? `${tz} (${offset})` : tz };
});

const COUNTRIES = [...COUNTRY_CODES]
  .map((code) => ({ code, name: regionLabel(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const REGION_TAG_SET = new Set<string>(REGION_TAGS);

interface SkillOption {
  value: string;
  label: string;
  category: string | null;
}

interface SkillOptionGroup {
  value: string;
  items: SkillOption[];
}

interface CountryOption {
  value: string;
  label: string;
}

// "a", "a and b", "a, b or c".
function joinNatural(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

function toDefaults(i: RoleFormInitial | undefined): FormValues {
  return {
    title: i?.title ?? '',
    roleTypeId: i?.roleTypeId ?? '',
    industryId: i?.industryId ?? null,
    seniority: (i?.seniority as Seniority | null) ?? null,
    employmentType: (i?.employmentType as EmploymentType | null) ?? null,
    // Salaries are Controller fields (no number coercion), so an empty one is
    // a plain null, matching the nullable Zod field.
    salaryMin: i?.salaryMin ?? null,
    salaryMax: i?.salaryMax ?? null,
    currency: i?.currency ?? 'USD',
    // react-hook-form coerces a literal `null` default on a registered number
    // field to 0 (Number(null) === 0); `undefined` avoids that and normalizes
    // to null once the field mounts, matching the nullable Zod field below.
    overlapHours: (i?.overlapHours ?? undefined) as number | null,
    timezone: i?.timezone ?? null,
    summary: i?.summary ?? '',
    skills: i?.skills ?? [],
    regions: i?.regions ?? [],
    sourceUrl: i?.sourceUrl ?? '',
    sourceCompanyName: i?.sourceCompanyName ?? '',
  };
}

interface RoleFormProps {
  taxonomy: RoleFormTaxonomy;
  /** Curated roles add source URL + company name. */
  curated?: boolean;
  initial?: RoleFormInitial;
  submitLabel: string;
  /** Shown on the submit button while `onSubmit` is pending. */
  submittingLabel?: string;
  onSubmit: (input: RoleInput | CuratedRoleInput) => Promise<void>;
  onCancel?: () => void;
  /** Server-side issues (path → message) to merge into the field errors. */
  serverIssues?: { path: string; message: string }[] | null;
  serverError?: string | null;
  /**
   * When set, shows a "posting is free, you have N credits" note above the
   * submit button and soft-disables submit until a title, seniority and at
   * least one skill are filled in (the schema itself doesn't require a
   * skill — this is a completion nudge, not validation).
   */
  creditsRemaining?: number;
  /** Free launch period (Q65): the credit note says introductions are free for now. */
  billingPaused?: boolean;
  /** Merged onto the root `<form>` element. */
  className?: string;
  /**
   * Company form only: "Can't find it? Add a role type" (Q77). Curators add
   * role types on the Taxonomy page instead.
   */
  /**
   * Company form only: "Can't find it? Add a skill" (Q79). The skill is
   * attached to this role at once and reviewed by a curator afterwards.
   * Curators add skills on the Taxonomy page instead.
   */
  onAddSkill?: (name: string) => Promise<AddedSkill>;
  onAddRoleType?: (input: {
    name: string;
    family: string;
    industryId: string | null;
    note: string | null;
  }) => Promise<AddedRoleType>;
}

// Structured role form shared by the company dashboard and the curator
// dashboard. Validates with the shared Zod schema before submitting.
export function RoleForm(props: RoleFormProps) {
  const { taxonomy, curated = false } = props;
  const [salaryPeriod, setSalaryPeriod] = useState<SalaryPeriod>('year');
  const schema = curated ? curatedRoleInputSchema : roleInputSchema;
  const { control, register, handleSubmit, watch, setValue, setError, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema) as unknown as Resolver<FormValues>,
      defaultValues: toDefaults(props.initial),
    });
  const errors = formState.errors;

  useEffect(() => {
    for (const issue of props.serverIssues ?? []) {
      const field = issue.path.split('.')[0] as keyof FormValues | undefined;
      if (field && !errors[field]) setError(field, { message: issue.message });
    }
    // Only re-run when a fresh batch of server issues arrives.
  }, [props.serverIssues]);

  const title = watch('title');
  const seniority = watch('seniority');
  const skills = watch('skills');
  const regions = watch('regions');
  const timezone = watch('timezone');
  const overlapHours = watch('overlapHours');
  const chosenSkills = new Set(skills.map((s) => s.skillId));
  // Types added from this form show up before the parent refetches.
  const [skillQuery, setSkillQuery] = useState('');
  const [addedSkills, setAddedSkills] = useState<AddedSkill[]>([]);
  // Skills added from this form show up before the parent refetches.
  const allSkills = [
    ...taxonomy.skills,
    ...addedSkills.filter((a) => !taxonomy.skills.some((k) => k.id === a.id)),
  ];
  const skillById = new Map(allSkills.map((s) => [s.id, s]));
  const [added, setAdded] = useState<AddedRoleType[]>([]);
  const roleTypes = [
    ...taxonomy.roleTypes,
    ...added.filter((a) => !taxonomy.roleTypes.some((rt) => rt.id === a.id)),
  ];
  const roleTypeId = watch('roleTypeId');
  const roleType = roleTypes.find((rt) => rt.id === roleTypeId);
  const industryId = watch('industryId');
  const industry = taxonomy.industries.find((i) => i.id === industryId) ?? null;
  const chosenIndustries = industry ? [industry] : [];
  // Industry first: its role types lead the list, nothing is hidden. A
  // rejected type is listed only while it is still the role's current one.
  const roleTypeOptions = groupRoleTypesFor(
    roleTypes.filter((rt) => rt.status !== 'rejected' || rt.id === roleTypeId),
    chosenIndustries,
  ).flatMap((g) =>
    g.roleTypes.map((rt) => ({ value: rt.id, label: roleTypeLabel(rt), group: g.heading })),
  );
  const families = [
    ...new Set(taxonomy.roleTypes.filter((rt) => rt.status === 'approved').map((rt) => rt.family)),
  ].sort((a, b) => a.localeCompare(b));
  // Skills for this role's family and industry first, then generic ones, then the rest.
  const skillGroups: SkillOptionGroup[] = groupSkillsFor(
    allSkills.filter((s) => !chosenSkills.has(s.id)),
    {
      families: roleType ? [roleType.family] : [],
      industries: chosenIndustries,
      roleTypes: roleType ? [roleType] : [],
    },
  ).map((g) => ({
    value: g.heading,
    items: g.skills.map((s) => ({
      value: s.id,
      label: s.status === 'pending' ? `${s.name} (pending review)` : s.name,
      category: s.category,
    })),
  }));
  // One-click chips for the chosen role type's skills not added yet.
  const suggestions = roleType
    ? suggestedSkills(allSkills, [roleType.id], industryId ? [industryId] : [], 12, chosenSkills)
    : [];
  const countries = regions.filter((c) => !REGION_TAG_SET.has(c));

  function addSkill(skillId: string) {
    const requiredCount = skills.filter((s) => s.required).length;
    setValue('skills', [...skills, { skillId, required: requiredCount < 3 }]);
  }

  function toggleRegionTag(tag: string) {
    if (tag === 'WORLDWIDE') {
      setValue('regions', regions.includes('WORLDWIDE') ? [] : ['WORLDWIDE']);
      return;
    }
    const withoutWorldwide = regions.filter((c) => c !== 'WORLDWIDE');
    setValue(
      'regions',
      withoutWorldwide.includes(tag)
        ? withoutWorldwide.filter((c) => c !== tag)
        : [...withoutWorldwide, tag],
    );
  }

  function addCountry(code: string) {
    const withoutWorldwide = regions.filter((c) => c !== 'WORLDWIDE');
    if (withoutWorldwide.includes(code)) return;
    setValue('regions', [...withoutWorldwide, code]);
  }

  const missing: string[] = [];
  if (props.creditsRemaining !== undefined) {
    if (!title.trim()) missing.push('a title');
    if (!seniority) missing.push('seniority');
    if (!skills.length) missing.push('1 skill');
  }
  const missingLine = missing.length ? `Add ${joinNatural(missing)} to post.` : '';

  const submit = handleSubmit(async (values) => {
    await props.onSubmit(values as unknown as RoleInput | CuratedRoleInput);
  });

  const fieldError = (name: keyof FormValues) =>
    errors[name] && <FieldError errors={[{ message: String(errors[name]?.message) }]} />;

  return (
    <form className={cn('grid gap-6', props.className)} onSubmit={submit} noValidate>
      {curated && (
        <FieldSet>
          <FieldLegend>Source</FieldLegend>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="sourceUrl">
                Original posting URL (company careers page or ATS — never a job board)
              </FieldLabel>
              <Input
                id="sourceUrl"
                type="url"
                placeholder="https://careers.example.com/jobs/123"
                {...register('sourceUrl')}
              />
              {fieldError('sourceUrl')}
            </Field>
            <Field>
              <FieldLabel htmlFor="sourceCompanyName">Company name (as on the posting)</FieldLabel>
              <Input id="sourceCompanyName" type="text" {...register('sourceCompanyName')} />
              {fieldError('sourceCompanyName')}
            </Field>
          </FieldGroup>
        </FieldSet>
      )}

      <FieldSet>
        <FieldLegend>The role</FieldLegend>
        <FieldGroup>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
            <Field>
              <FieldLabel htmlFor="title">Title</FieldLabel>
              <Input
                id="title"
                type="text"
                placeholder="e.g. Backend Engineer"
                {...register('title')}
              />
              {fieldError('title')}
            </Field>
            <Field>
              <FieldLabel>Seniority</FieldLabel>
              <Controller
                control={control}
                name="seniority"
                render={({ field }) => (
                  <Select
                    items={SENIORITIES.map((s) => ({ label: SENIORITY_LABELS[s], value: s }))}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Seniority"
                      aria-invalid={!!errors.seniority}
                    >
                      <SelectValue placeholder="Select seniority" />
                    </SelectTrigger>
                    <SelectContent>
                      {SENIORITIES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {SENIORITY_LABELS[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {fieldError('seniority')}
            </Field>
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
            <Field>
              <FieldLabel>Industry</FieldLabel>
              <Controller
                control={control}
                name="industryId"
                render={({ field }) => (
                  <SearchableSelect
                    options={taxonomy.industries.map((i) => ({ value: i.id, label: i.name }))}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Search industries"
                    aria-label="Industry"
                    aria-invalid={!!errors.industryId}
                  />
                )}
              />
              {fieldError('industryId')}
            </Field>
            <Field>
              <FieldLabel>Role type</FieldLabel>
              <Controller
                control={control}
                name="roleTypeId"
                render={({ field }) => (
                  <SearchableSelect
                    options={roleTypeOptions}
                    value={field.value}
                    onChange={(v) => field.onChange(v ?? '')}
                    placeholder="Search role types"
                    emptyText={props.onAddRoleType ? 'No matches. Add it below.' : 'No matches.'}
                    aria-label="Role type"
                    aria-invalid={!!errors.roleTypeId}
                  />
                )}
              />
              {fieldError('roleTypeId')}
            </Field>
            <Field>
              <FieldLabel>Employment</FieldLabel>
              <Controller
                control={control}
                name="employmentType"
                render={({ field }) => (
                  <Select
                    items={EMPLOYMENT_TYPES.map((t) => ({ label: EMPLOYMENT_LABELS[t], value: t }))}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Employment"
                      aria-invalid={!!errors.employmentType}
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {EMPLOYMENT_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {fieldError('employmentType')}
            </Field>
          </div>
          {roleType?.status === 'rejected' && (
            <p className="text-sm text-destructive">
              We could not keep &ldquo;{roleType.name}&rdquo; as a role type. Choose another one
              before saving.
            </p>
          )}
          {roleType?.status === 'pending' && (
            <p className="text-xs text-muted-foreground">
              &ldquo;{roleType.name}&rdquo; is waiting for a curator&apos;s review. You can post
              with it now.
            </p>
          )}
          {props.onAddRoleType && (
            <AddRoleType
              families={families}
              industry={industry}
              existing={roleTypes.filter((rt) => rt.status !== 'rejected')}
              onAdd={props.onAddRoleType}
              onAdded={(rt) => {
                setAdded((prev) => (prev.some((p) => p.id === rt.id) ? prev : [...prev, rt]));
                setValue('roleTypeId', rt.id, { shouldValidate: true });
              }}
            />
          )}
          <Field>
            <FieldLabel htmlFor="summary">
              Summary — 1–2 sentences in your own words
              {curated ? ' (never the original description)' : ''}
            </FieldLabel>
            <Textarea
              id="summary"
              rows={3}
              placeholder="What this person will own in their first 6 months."
              {...register('summary')}
            />
            {fieldError('summary')}
          </Field>
        </FieldGroup>
      </FieldSet>

      <FieldSet>
        <FieldLegend>Pay and hours</FieldLegend>
        <FieldGroup>
          <SalaryPeriodToggle value={salaryPeriod} onChange={setSalaryPeriod} />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
            <Field>
              <FieldLabel htmlFor="salaryMin">Salary min</FieldLabel>
              <Controller
                control={control}
                name="salaryMin"
                render={({ field }) => (
                  <Input
                    id="salaryMin"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    icon={<BanknoteIcon />}
                    placeholder={salaryPeriod === 'month' ? '6000' : '70000'}
                    value={salaryForPeriod(field.value, salaryPeriod) ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        salaryToAnnual(
                          e.target.value === '' ? null : Number(e.target.value),
                          salaryPeriod,
                        ),
                      )
                    }
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FieldDescription>
                {salaryPeriod === 'month' ? 'Per month, saved as annual' : 'Annual'}
              </FieldDescription>
              {fieldError('salaryMin')}
            </Field>
            <Field>
              <FieldLabel htmlFor="salaryMax">Salary max</FieldLabel>
              <Controller
                control={control}
                name="salaryMax"
                render={({ field }) => (
                  <Input
                    id="salaryMax"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    icon={<BanknoteIcon />}
                    placeholder={salaryPeriod === 'month' ? '7500' : '90000'}
                    value={salaryForPeriod(field.value, salaryPeriod) ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        salaryToAnnual(
                          e.target.value === '' ? null : Number(e.target.value),
                          salaryPeriod,
                        ),
                      )
                    }
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FieldDescription>
                {salaryPeriod === 'month' ? 'Per month, saved as annual' : 'Annual'}
              </FieldDescription>
              {fieldError('salaryMax')}
            </Field>
            <Field>
              <FieldLabel htmlFor="currency">Currency</FieldLabel>
              <Input
                id="currency"
                type="text"
                maxLength={3}
                {...register('currency', {
                  setValueAs: (v: string) => (v.trim() === '' ? null : v.toUpperCase()),
                })}
              />
              {fieldError('currency')}
            </Field>
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
            <Field>
              <FieldLabel htmlFor="timezone">Team timezone</FieldLabel>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <SearchableSelect
                    options={TIMEZONE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    clearable
                    placeholder="Search timezones"
                    id="timezone"
                  />
                )}
              />
              {fieldError('timezone')}
            </Field>
            <Field>
              <div className="flex items-center gap-1.5">
                <FieldLabel htmlFor="overlapHours">Overlap needed (hours)</FieldLabel>
                <Tooltip>
                  <TooltipTrigger
                    type="button"
                    aria-label="What is overlap needed?"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <InfoIcon aria-hidden="true" className="size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Working hours per day the hire must share with your team. Blank means no
                    requirement.
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                id="overlapHours"
                type="number"
                min={0}
                max={12}
                inputMode="numeric"
                {...register('overlapHours', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
              />
              {fieldError('overlapHours')}
            </Field>
          </div>
          {timezone && overlapHours != null && (
            <p className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground tabular-nums">
              <Clock3Icon aria-hidden="true" className="size-4" />
              {overlapHours}h overlap with {timezone}
            </p>
          )}
        </FieldGroup>
      </FieldSet>

      <FieldSet>
        <FieldLegend>Skills</FieldLegend>
        <FieldDescription>
          We match candidates to this role using the skills you list here. Mark each one Required or
          Nice to have — required skills count for more, so keep them to the 3 to 5 the role can’t
          do without.
        </FieldDescription>
        <FieldContent className="gap-3">
          {suggestions.length > 0 && roleType && (
            <div className="grid gap-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {suggestedHeading([roleType])}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((sk) => (
                  <button
                    key={sk.id}
                    type="button"
                    onClick={() => addSkill(sk.id)}
                    className={cn(badgeVariants({ variant: 'outline' }), 'cursor-pointer')}
                  >
                    + {sk.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Combobox
            key={skills.length}
            items={skillGroups}
            value={null as SkillOption | null}
            inputValue={skillQuery}
            onInputValueChange={(v, details) => {
              if (details.reason === 'item-press') return;
              setSkillQuery(v);
            }}
            onValueChange={(item: SkillOption | null) => {
              if (!item) return;
              addSkill(item.value);
              setSkillQuery('');
            }}
            isItemEqualToValue={(a: SkillOption, b: SkillOption) => a.value === b.value}
          >
            <ComboboxInput
              startIcon={<SearchIcon />}
              placeholder="Search skills, e.g. PostgreSQL"
              aria-label="Search skills"
            />
            <ComboboxContent>
              <ComboboxEmpty>
                {props.onAddSkill ? 'No matches. Add it below.' : 'No matching skills.'}
              </ComboboxEmpty>
              <ComboboxList>
                {(group: SkillOptionGroup) => (
                  <ComboboxGroup key={group.value} items={group.items}>
                    <ComboboxLabel>{group.value}</ComboboxLabel>
                    {group.items.map((item) => (
                      <ComboboxItem key={item.value} value={item}>
                        {item.label}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          {props.onAddSkill && (
            <AddSkill
              suggestedName={skillQuery}
              existing={allSkills.filter((s) => !chosenSkills.has(s.id))}
              onAdd={props.onAddSkill}
              onAdded={(sk) => {
                setAddedSkills((prev) => (prev.some((p) => p.id === sk.id) ? prev : [...prev, sk]));
                if (!chosenSkills.has(sk.id)) addSkill(sk.id);
              }}
            />
          )}
          {skills.length > 0 ? (
            <div className="divide-y divide-border rounded-lg border border-border">
              {skills.map((s, index) => {
                const meta = skillById.get(s.skillId);
                return (
                  <div key={s.skillId} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-2">
                      <span className="font-medium">{meta?.name ?? s.skillId}</span>
                      {meta?.status === 'pending' && (
                        <span className="text-xs text-muted-foreground">pending review</span>
                      )}
                      {meta?.category && (
                        <span className="text-xs text-muted-foreground">{meta.category}</span>
                      )}
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={`${meta?.name ?? s.skillId} importance`}
                      className="inline-flex gap-0.5 rounded-md border border-border bg-muted p-0.5"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={s.required}
                        onClick={() => setValue(`skills.${index}.required`, true)}
                        className={cn(
                          'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors',
                          s.required
                            ? 'bg-card text-foreground shadow-xs'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        Required
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={!s.required}
                        onClick={() => setValue(`skills.${index}.required`, false)}
                        className={cn(
                          'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors',
                          !s.required
                            ? 'bg-card text-foreground shadow-xs'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        Nice to have
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove ${meta?.name ?? s.skillId}`}
                      title="Remove"
                      onClick={() =>
                        setValue(
                          'skills',
                          skills.filter((x) => x.skillId !== s.skillId),
                        )
                      }
                    >
                      <XIcon />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No skills yet. Add at least 1 to post.</p>
          )}
          {skills.length > 0 && (
            <p className="text-xs text-muted-foreground tabular-nums">
              {skills.filter((s) => s.required).length} required ·{' '}
              {skills.filter((s) => !s.required).length} nice to have
            </p>
          )}
        </FieldContent>
        {fieldError('skills')}
      </FieldSet>

      <FieldSet>
        <FieldLegend>Where candidates can live</FieldLegend>
        <FieldGroup>
          <Field>
            <FieldLabel>Regions</FieldLabel>
            <FieldDescription>Pick one or more, or keep Worldwide.</FieldDescription>
            <div className="flex flex-wrap gap-2">
              {REGION_TAGS.map((tag) => {
                const selected =
                  tag === 'WORLDWIDE'
                    ? regions.length === 0 || regions.includes(tag)
                    : regions.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleRegionTag(tag)}
                    className={cn(
                      badgeVariants({ variant: selected ? 'default' : 'outline' }),
                      'cursor-pointer',
                    )}
                  >
                    {regionLabel(tag)}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field>
            <FieldLabel>Countries (optional)</FieldLabel>
            <FieldDescription>
              Also accept candidates from specific countries outside the regions above.
            </FieldDescription>
            <Combobox
              key={countries.length}
              items={COUNTRIES.filter((c) => !regions.includes(c.code)).map((c) => ({
                value: c.code,
                label: c.name,
              }))}
              value={null as CountryOption | null}
              onValueChange={(item: CountryOption | null) => item && addCountry(item.value)}
              isItemEqualToValue={(a: CountryOption, b: CountryOption) => a.value === b.value}
            >
              <ComboboxInput
                startIcon={<GlobeIcon />}
                placeholder="Add a country"
                aria-label="Add a country"
              />
              <ComboboxContent>
                <ComboboxEmpty>No matches.</ComboboxEmpty>
                <ComboboxList>
                  {(item: CountryOption) => (
                    <ComboboxItem key={item.value} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            {countries.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {countries.map((code) => (
                  <li key={code}>
                    <Badge variant="secondary" className="h-auto gap-2 py-1">
                      {regionLabel(code)}
                      <button
                        type="button"
                        aria-label={`Remove ${regionLabel(code)}`}
                        onClick={() =>
                          setValue(
                            'regions',
                            regions.filter((c) => c !== code),
                          )
                        }
                      >
                        <XIcon className="size-3" />
                      </button>
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </FieldGroup>
        {fieldError('regions')}
      </FieldSet>

      <div data-slot="role-form-footer" className="flex flex-col gap-4">
        {props.creditsRemaining !== undefined && (
          <div className="flex items-start gap-3 text-sm">
            <TicketIcon
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0 text-muted-foreground"
            />
            <span className="text-pretty text-muted-foreground">
              {props.billingPaused ? (
                'Posting is free, and so are introductions during launch. Once billing starts, a role uses 1 role credit at its first introduction.'
              ) : (
                <>
                  Posting is free. You only pay when we send the first introduction for this role —
                  1 role credit. You have{' '}
                  {props.creditsRemaining === 1 ? '1 credit' : `${props.creditsRemaining} credits`}.
                </>
              )}
            </span>
          </div>
        )}

        {props.serverError && <p className="text-destructive text-sm">{props.serverError}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Button
            type="submit"
            disabled={
              formState.isSubmitting || (props.creditsRemaining !== undefined && missing.length > 0)
            }
          >
            {formState.isSubmitting ? (props.submittingLabel ?? 'Saving…') : props.submitLabel}
          </Button>
          {props.onCancel && (
            <Button type="button" variant="ghost" onClick={props.onCancel}>
              Cancel
            </Button>
          )}
          {props.creditsRemaining !== undefined && missingLine && (
            <span className="text-xs text-muted-foreground">{missingLine}</span>
          )}
        </div>
      </div>
    </form>
  );
}
